import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import { toast } from 'react-toastify'
import * as types from '../../../actions/index.types'
import * as req from '../../../../axios/request/SocietyPortal/AttendanceReqs/AttendanceReqs'

// The backend puts a stable machine-readable `code` inside the error
// envelope's data ({ success:false, message, data:{ code, ... } }). Branch on
// that, never on message text. On axios rejection the envelope is at
// e.response.data (the response interceptor only unwraps 2xx bodies).
const errorInfo = (e) => {
    const envelope = e?.response?.data
    return {
        message: envelope?.message || e?.message || 'Something went wrong',
        code: envelope?.data?.code || null,
        // 409s carry the current `attendance` state, GEOFENCE_BLOCKED carries
        // `geofence` — pages use these to re-sync a stale UI.
        data: envelope?.data || null,
    }
}

function* getAttendanceReport(action) {
    try {
        const result = yield call(req.getAttendanceReportReq, action)
        if (result.success === true) {
            yield put({ type: types.SOCIETY_GET_ATTENDANCE_REPORT_SUCCESS, payload: result })
        } else {
            yield put({ type: types.SOCIETY_GET_ATTENDANCE_REPORT_FAILURE, message: result?.message })
            toast.error(result?.message || 'Failed to fetch attendance report')
        }
    } catch (e) {
        console.log(e)
        yield put({ type: types.SOCIETY_GET_ATTENDANCE_REPORT_FAILURE, message: e?.message })
        toast.error(e?.response?.data?.message || 'Something went wrong')
    }
}

// Expected non-error states: the user simply isn't an employee here, or the
// society hasn't enabled attendance. The page renders a friendly screen for
// these — no toast.
const EXPECTED_STATUS_CODES = ['NOT_AN_EMPLOYEE', 'ATTENDANCE_DISABLED']

function* getMyAttendanceStatus(action) {
    try {
        const result = yield call(req.getMyAttendanceStatusReq, action)
        if (result?.success === true) {
            yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_STATUS_SUCCESS, payload: result.data })
            action.data?.onSuccess?.(result.data)
        } else {
            yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_STATUS_FAILURE, error: { message: result?.message, code: null } })
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_STATUS_FAILURE, error: info })
        if (!EXPECTED_STATUS_CODES.includes(info.code)) {
            toast.error(info.message)
        }
    }
}

// Codes the punch screen handles itself (re-sync, geofence banner, selfie
// prompt, work log modal) — the saga stays silent for these and lets onError
// drive the UI.
const PAGE_HANDLED_PUNCH_CODES = [
    'ALREADY_PUNCHED_IN',
    'NOT_PUNCHED_IN',
    'GEOFENCE_BLOCKED',
    'SELFIE_REQUIRED',
    'WORK_LOG_REQUIRED',
    'WORK_LOG_NOT_ALLOWED',
]

function* punchAttendance(action) {
    try {
        const result = yield call(req.punchAttendanceReq, action)
        if (result?.success === true) {
            // duplicate:true means a retried tap was replayed (HTTP 200) —
            // still success, just don't announce it as a fresh punch.
            yield put({ type: types.SOCIETY_PUNCH_ATTENDANCE_SUCCESS, payload: result.data })
            toast.success(result?.message || 'Punch recorded')
            action.data?.onSuccess?.(result.data)
        } else {
            const info = { message: result?.message || 'Punch failed', code: null, data: result?.data || null }
            yield put({ type: types.SOCIETY_PUNCH_ATTENDANCE_FAILURE, error: info })
            toast.error(info.message)
            action.data?.onError?.(info)
        }
    } catch (e) {
        console.log(e)
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_PUNCH_ATTENDANCE_FAILURE, error: info })
        if (!PAGE_HANDLED_PUNCH_CODES.includes(info.code)) {
            toast.error(info.message)
        }
        action.data?.onError?.(info)
    }
}

function* getMyAttendancePunches(action) {
    try {
        const result = yield call(req.getMyAttendancePunchesReq, action)
        if (result?.success === true) {
            yield put({
                type: types.SOCIETY_GET_MY_ATTENDANCE_PUNCHES_SUCCESS,
                payload: { rows: result.data, pagination: result.meta?.pagination },
            })
        } else {
            yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_PUNCHES_FAILURE, message: result?.message })
            toast.error(result?.message || 'Failed to fetch attendance records')
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_PUNCHES_FAILURE, message: info.message })
        if (!EXPECTED_STATUS_CODES.includes(info.code)) {
            toast.error(info.message)
        }
    }
}

function* getMyAttendanceSummary(action) {
    try {
        const result = yield call(req.getMyAttendanceSummaryReq, action)
        if (result?.success === true) {
            yield put({
                type: types.SOCIETY_GET_MY_ATTENDANCE_SUMMARY_SUCCESS,
                payload: {
                    days: result.data?.days || [],
                    totals: result.data?.totals || null,
                    pagination: result.meta?.pagination,
                },
            })
        } else {
            yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_SUMMARY_FAILURE, message: result?.message })
            toast.error(result?.message || 'Failed to fetch attendance summary')
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_GET_MY_ATTENDANCE_SUMMARY_FAILURE, message: info.message })
        if (!EXPECTED_STATUS_CODES.includes(info.code)) {
            toast.error(info.message)
        }
    }
}

function* updateAttendancePunch(action) {
    try {
        const result = yield call(req.updateAttendancePunchReq, action)
        if (result?.success === true) {
            yield put({ type: types.SOCIETY_UPDATE_ATTENDANCE_PUNCH_SUCCESS, payload: result.data })
            toast.success(result?.message || 'Punch updated')
            action.data?.onSuccess?.(result.data)
        } else {
            const info = { message: result?.message || 'Failed to update punch', code: null, data: result?.data || null }
            yield put({ type: types.SOCIETY_UPDATE_ATTENDANCE_PUNCH_FAILURE, error: info })
            toast.error(info.message)
            action.data?.onError?.(info)
        }
    } catch (e) {
        console.log(e)
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_UPDATE_ATTENDANCE_PUNCH_FAILURE, error: info })
        // PUNCH_REJECTED (409): a rejected punch can't be edited; PUNCH_NOT_FOUND
        // (404): stale row. Both mean the caller should refresh its list — the
        // toast tells the admin why, onError lets the screen refetch.
        toast.error(info.message)
        action.data?.onError?.(info)
    }
}

// ---- Admin attendance management ------------------------------------------

function* getAdminAttendanceDaily(action) {
    try {
        const result = yield call(req.getAdminAttendanceDailyReq, action)
        if (result?.success === true) {
            yield put({
                type: types.SOCIETY_GET_ADMIN_ATTENDANCE_DAILY_SUCCESS,
                payload: { rows: result.data, pagination: result.meta?.pagination },
            })
        } else {
            yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_DAILY_FAILURE, message: result?.message })
            toast.error(result?.message || 'Failed to fetch daily attendance')
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_DAILY_FAILURE, message: info.message })
        toast.error(info.message)
    }
}

function* getAdminAttendanceSummary(action) {
    try {
        const result = yield call(req.getAdminAttendanceSummaryReq, action)
        if (result?.success === true) {
            yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_SUMMARY_SUCCESS, payload: result.data })
        } else {
            yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_SUMMARY_FAILURE, message: result?.message })
        }
    } catch (e) {
        // Tiles are decorative next to the grid — fail without a toast storm.
        yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_SUMMARY_FAILURE, message: e?.message })
    }
}

function* getAdminAttendancePunches(action) {
    try {
        const result = yield call(req.getAdminAttendancePunchesReq, action)
        if (result?.success === true) {
            yield put({
                type: types.SOCIETY_GET_ADMIN_ATTENDANCE_PUNCHES_SUCCESS,
                payload: { rows: result.data, pagination: result.meta?.pagination },
            })
        } else {
            yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_PUNCHES_FAILURE, message: result?.message })
            toast.error(result?.message || 'Failed to fetch punches')
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_GET_ADMIN_ATTENDANCE_PUNCHES_FAILURE, message: info.message })
        toast.error(info.message)
    }
}

function* createManualPunch(action) {
    try {
        const result = yield call(req.createManualPunchReq, action)
        if (result?.success === true) {
            yield put({ type: types.SOCIETY_CREATE_MANUAL_PUNCH_SUCCESS, payload: result.data })
            toast.success(result?.message || 'Manual punch recorded')
            action.data?.onSuccess?.(result.data)
        } else {
            const info = { message: result?.message || 'Failed to record punch', code: null, data: result?.data || null }
            yield put({ type: types.SOCIETY_CREATE_MANUAL_PUNCH_FAILURE, error: info })
            toast.error(info.message)
            action.data?.onError?.(info)
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: types.SOCIETY_CREATE_MANUAL_PUNCH_FAILURE, error: info })
        toast.error(info.message)
        action.data?.onError?.(info)
    }
}

// Approve/reject share a worker shape. On 409 PUNCH_ALREADY_DECIDED the
// response's data.punch carries the current state — the caller's onError
// should refresh the queue instead of leaving the stale entry.
function* decidePunch(action, requestFn, successType, failureType) {
    try {
        const result = yield call(requestFn, action)
        if (result?.success === true) {
            yield put({ type: successType, payload: result.data })
            toast.success(result?.message || 'Punch updated')
            action.data?.onSuccess?.(result.data)
        } else {
            const info = { message: result?.message || 'Failed to update punch', code: null, data: result?.data || null }
            yield put({ type: failureType, error: info })
            toast.error(info.message)
            action.data?.onError?.(info)
        }
    } catch (e) {
        const info = errorInfo(e)
        yield put({ type: failureType, error: info })
        toast.error(info.message)
        action.data?.onError?.(info)
    }
}

function* approveAttendancePunch(action) {
    yield* decidePunch(action, req.approveAttendancePunchReq,
        types.SOCIETY_APPROVE_ATTENDANCE_PUNCH_SUCCESS, types.SOCIETY_APPROVE_ATTENDANCE_PUNCH_FAILURE)
}

function* rejectAttendancePunch(action) {
    yield* decidePunch(action, req.rejectAttendancePunchReq,
        types.SOCIETY_REJECT_ATTENDANCE_PUNCH_SUCCESS, types.SOCIETY_REJECT_ATTENDANCE_PUNCH_FAILURE)
}

function* getAttendanceEnums() {
    try {
        const result = yield call(req.getAttendanceEnumsReq)
        if (result?.success === true) {
            yield put({ type: types.SOCIETY_GET_ATTENDANCE_ENUMS_SUCCESS, payload: result.data })
        } else {
            yield put({ type: types.SOCIETY_GET_ATTENDANCE_ENUMS_FAILURE, message: result?.message })
        }
    } catch (e) {
        // Enums only feed filter dropdowns — fail quietly, filters fall back to free text.
        yield put({ type: types.SOCIETY_GET_ATTENDANCE_ENUMS_FAILURE, message: e?.message })
    }
}

export default function* AttendanceSaga() {
    yield takeLatest(types.SOCIETY_GET_ATTENDANCE_REPORT, getAttendanceReport)
    yield takeLatest(types.SOCIETY_GET_MY_ATTENDANCE_STATUS, getMyAttendanceStatus)
    // takeEvery so a punch response is never dropped mid-flight — the button
    // is disabled while one is pending, and client_request_id makes replays safe.
    yield takeEvery(types.SOCIETY_PUNCH_ATTENDANCE, punchAttendance)
    yield takeEvery(types.SOCIETY_UPDATE_ATTENDANCE_PUNCH, updateAttendancePunch)
    yield takeLatest(types.SOCIETY_GET_MY_ATTENDANCE_PUNCHES, getMyAttendancePunches)
    yield takeLatest(types.SOCIETY_GET_MY_ATTENDANCE_SUMMARY, getMyAttendanceSummary)
    yield takeLatest(types.SOCIETY_GET_ATTENDANCE_ENUMS, getAttendanceEnums)
    yield takeLatest(types.SOCIETY_GET_ADMIN_ATTENDANCE_DAILY, getAdminAttendanceDaily)
    yield takeLatest(types.SOCIETY_GET_ADMIN_ATTENDANCE_SUMMARY, getAdminAttendanceSummary)
    yield takeLatest(types.SOCIETY_GET_ADMIN_ATTENDANCE_PUNCHES, getAdminAttendancePunches)
    yield takeEvery(types.SOCIETY_CREATE_MANUAL_PUNCH, createManualPunch)
    yield takeEvery(types.SOCIETY_APPROVE_ATTENDANCE_PUNCH, approveAttendancePunch)
    yield takeEvery(types.SOCIETY_REJECT_ATTENDANCE_PUNCH, rejectAttendancePunch)
}
